import { EventType } from "@/analytics/analytics-events";
import { Mode, ModeTransitionProps } from "@/modes/mode";
import { changeMode } from "@/store/mode-slice";
import { selectElementToAlignWithControlPointsAlignment } from "@/store/modes/control-points-alignment-mode-selectors";
import {
  resetControlPointsAlignment,
  setControlPointsSheetElevation,
} from "@/store/modes/control-points-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectIElementWorldMatrix4 } from "@/utils/transform-conversion-parsed";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { isIElementAreaSection } from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { useEffect, useMemo } from "react";
import {
  AlignmentTransform,
  matrix4ToAlignmentTransform,
} from "../alignment-modes-commons/alignment-transform";
import { useCurrentScene } from "../mode-data-context";
import { ControlPointsAlignmentModeOverlay } from "./control-points-alignment-mode-overlay";
import { ControlPointsAlignmentModeScene } from "./control-points-alignment-mode-scene";

export const controlPointsAlignmentMode: Mode = {
  name: "controlPointsAlignment",
  ModeScene: ControlPointsAlignmentModeScene,
  ModeOverlay: ControlPointsAlignmentModeOverlay,
  hasCustomCanvasStyle: true,
  ModeTransition: ControlPointsAlignmentModeTransition,
  exclusive: {
    title: "Align using control points",
    onBack({ dispatch, store }) {
      const storeState = store.getState();
      const elementToAlignId =
        selectElementToAlignWithControlPointsAlignment(storeState);
      const alignedElement = selectIElement(elementToAlignId)(storeState);

      dispatch(changeMode("start"));
      dispatch(resetControlPointsAlignment());

      Analytics.track(
        alignedElement && isIElementAreaSection(alignedElement)
          ? EventType.exitControlPointsAlignmentArea
          : EventType.exitControlPointsAlignmentSheet,
      );

      return Promise.resolve();
    },
  },
};

function ControlPointsAlignmentModeTransition({
  onCompleted,
}: ModeTransitionProps): null {
  const elementToAlignId = useAppSelector(
    selectElementToAlignWithControlPointsAlignment,
  );
  const alignedElement = useAppSelector(selectIElement(elementToAlignId));
  assert(alignedElement, "invalid aligned element");

  // all sheets in the area must have the same elevation
  // so we can use the elevation of the area to set the elevation of any aligned sheet
  const area = useAppSelector(
    selectAncestor(alignedElement, isIElementAreaSection),
  );

  assert(area, "invalid area");

  const currentScene = useCurrentScene();

  // Pre-populate elevation of the aligned element:
  // use previous area elevation for area alignment or
  // the elevation of the active sheet in area to sheet alignment
  const alignedElementMatrix = useAppSelector(
    selectIElementWorldMatrix4(
      isIElementAreaSection(alignedElement) ||
        !currentScene.activeSheetFor2DNavigation
        ? area.id
        : currentScene.activeSheetFor2DNavigation.id,
    ),
  );

  const alignedElementTransform = useMemo(
    (): AlignmentTransform => matrix4ToAlignmentTransform(alignedElementMatrix),
    [alignedElementMatrix],
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(
      setControlPointsSheetElevation(alignedElementTransform.position[1]),
    );
    onCompleted();
  }, [alignedElementTransform.position, dispatch, onCompleted]);

  return null;
}
